import { memo, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { Stack, useMantineTheme } from '@mantine/core';
import { useShallow } from 'zustand/react/shallow';

import { GameSectionsContent } from '~/domains/common/components/GameSectionsList/GameSectionsList';
import type { GameSection } from '~/domains/common/components/GameSectionsList/GameSectionsList';
import { useIsDesktop } from '~/domains/common/hooks/mediaQueries';
import useProtectedGuard from '~/domains/protectionGuards/hooks/useProtectedGuard';
import { ProtectionGuardType } from '~/domains/protectionGuards/types';

import { MarketCard } from '../MarketCard/MarketCard';
import { MarketSelectionDrawer } from '../MarketSelectionDrawer/MarketSelectionDrawer';
import { MarketsTermsFooter } from '../MarketsTermsFooter/MarketsTermsFooter';
import {
  useMarketSelectionStore,
  type MarketType,
  type TeamType,
} from '../../state/marketSelection.store';
import { resolveGameSelection } from '../../hooks/marketSelectionFactories';
import {
  useMarketGroupVisibility,
  type ObserveMarketGroupRef,
} from '../../hooks/useMarketGroupVisibility';
import { useMarketLocationAllowed } from '../../hooks/useMarketLocationAllowed';

type MarketItem = {
  marketGroupId: string;
};

type Section = GameSection<MarketItem>;

type MarketSectionsListProps = {
  sections: Section[];
  lastItemRef?: (node: HTMLDivElement | null) => void;
  loadingMore?: ReactNode;
};

type MarketCardItemProps = {
  marketGroupId: string;
  lastItemRef?: (node: HTMLDivElement | null) => void;
  isLast?: boolean;
  observeMarketGroup: ObserveMarketGroupRef;
  onMarketClick: (
    marketGroupId: string,
    marketId: string,
    market: MarketType,
    team: TeamType
  ) => void;
};

const MarketCardItem = memo(
  ({
    marketGroupId,
    lastItemRef,
    isLast,
    observeMarketGroup,
    onMarketClick,
  }: MarketCardItemProps) => {
    const handleMarketClick = useCallback(
      (market: MarketType, team: TeamType, marketId: string) =>
        onMarketClick(marketGroupId, marketId, market, team),
      [marketGroupId, onMarketClick]
    );

    const observeRef = observeMarketGroup(marketGroupId);

    const handleRef = useCallback(
      (node: HTMLDivElement | null) => {
        observeRef(node);

        if (isLast && lastItemRef) {
          lastItemRef(node);
        }
      },
      [observeRef, isLast, lastItemRef]
    );

    return (
      <MarketCard ref={handleRef} marketGroupId={marketGroupId} onMarketClick={handleMarketClick} />
    );
  }
);

MarketCardItem.displayName = 'MarketCardItem';

interface MarketSectionsContentProps extends MarketSectionsListProps {
  observeMarketGroup: ObserveMarketGroupRef;
  onMarketClick: (
    marketGroupId: string,
    marketId: string,
    market: MarketType,
    team: TeamType
  ) => void;
}

const MarketSectionsContent = memo(
  ({
    sections,
    lastItemRef,
    loadingMore,
    observeMarketGroup,
    onMarketClick,
  }: MarketSectionsContentProps) => {
    const theme = useMantineTheme();

    return (
      <GameSectionsContent
        sections={sections}
        lastItemRef={lastItemRef}
        loadingMore={loadingMore}
        footer={<MarketsTermsFooter variant="browse" />}
        renderSectionItems={(items, sectionLastItemRef) => (
          <Stack spacing={theme.other.spacing._12}>
            {items.map((item, index) => (
              <MarketCardItem
                key={item.marketGroupId}
                marketGroupId={item.marketGroupId}
                observeMarketGroup={observeMarketGroup}
                onMarketClick={onMarketClick}
                isLast={index === items.length - 1}
                lastItemRef={sectionLastItemRef}
              />
            ))}
          </Stack>
        )}
      />
    );
  }
);

MarketSectionsContent.displayName = 'MarketSectionsContent';

function MarketSectionsListComponent({
  sections,
  lastItemRef,
  loadingMore,
}: MarketSectionsListProps) {
  const { toggleSelection, openDrawer } = useMarketSelectionStore(
    useShallow((state) => ({
      toggleSelection: state.toggleSelection,
      openDrawer: state.openDrawer,
    }))
  );
  const isBelowXL = useIsDesktop();
  const observeMarketGroup = useMarketGroupVisibility();

  const { isAllowed: isLocationAllowed, locationRestrictionModal } = useMarketLocationAllowed();

  const { runProtectionChecks } = useProtectedGuard({
    action: 'select a market',
    protectionLevels: [ProtectionGuardType.GEOCOMPLY, ProtectionGuardType.CONTEST],
    allowAnnonymous: true,
    isContestLocationRestrictedOverride: !isLocationAllowed,
    locationRestrictionModal,
  });

  const runProtectionChecksRef = useRef(runProtectionChecks);
  const isBelowXLRef = useRef(isBelowXL);

  useEffect(() => {
    runProtectionChecksRef.current = runProtectionChecks;
  }, [runProtectionChecks]);

  useEffect(() => {
    isBelowXLRef.current = isBelowXL;
  }, [isBelowXL]);

  const handleMarketClick = useCallback(
    async (marketGroupId: string, _marketId: string, market: MarketType, team: TeamType) => {
      const isAccessible = await runProtectionChecksRef.current();
      if (!isAccessible) {
        return;
      }

      const selection = resolveGameSelection(marketGroupId, market, team);
      if (!selection) return;

      toggleSelection(selection);
      if (isBelowXLRef.current) {
        openDrawer();
      }
    },
    [toggleSelection, openDrawer]
  );

  return (
    <>
      <MarketSectionsContent
        sections={sections}
        lastItemRef={lastItemRef}
        loadingMore={loadingMore}
        observeMarketGroup={observeMarketGroup}
        onMarketClick={handleMarketClick}
      />
      <MarketSelectionDrawer />
    </>
  );
}

export const MarketSectionsList = memo(MarketSectionsListComponent);

MarketSectionsList.displayName = 'MarketSectionsList';
